import { isNumber, isString } from '@/libs/test';

/**
 * @description 获取对象的嵌套属性，支持 'a.b.c' 形式
 * @param obj 目标对象
 * @param key 属性路径
 * @returns 属性值或 undefined
 */
export function getProperty(obj: Record<string, any>, key: string): any {
  if (!obj || !key) return undefined;
  return key.split('.').reduce((acc, cur) => (acc ? acc[cur] : undefined), obj);
}

/**
 * @description 设置对象的嵌套属性，支持 'a.b.c' 形式
 * @param obj 目标对象
 * @param key 属性路径
 * @param value 设置的值
 */
export function setProperty(obj: Record<string, any>, key: string, value: any): void {
  if (!obj || !key) return;
  const keys = key.split('.');
  let temp = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (typeof temp[keys[i]] !== 'object' || temp[keys[i]] === null) {
      temp[keys[i]] = {};
    }
    temp = temp[keys[i]];
  }
  temp[keys[keys.length - 1]] = value;
}

/**
 * 判断是否为空
 */
export function isEmpty(value: any): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (typeof value === 'boolean') return !value;
  if (typeof value === 'number') return value === 0 || isNaN(value);
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * @description 显示消息提示框
 * @param title 提示内容
 * @param callback 在延迟时间之后调用
 * @param duration 提示的延迟时间，单位毫秒，默认：2000
 */
export function toast(
  title: string,
  callback: undefined | ((...args: any[]) => any) = undefined,
  duration: number = 2000,
) {
  return uni.showToast({
    duration,
    icon: 'none',
    title: String(title),
    success: () => {
      if (callback && typeof callback === 'function') {
        setTimeout(callback, duration);
      }
    },
  });
}

/**
 * @description 对象转url参数
 * @param data 对象
 * @param isPrefix 是否自动加上"?"
 * @param arrayFormat 规则 indices|brackets|repeat|comma
 * @returns url参数字符串
 */
export function queryParams(
  data: Record<string, any> = {},
  isPrefix: boolean = true,
  arrayFormat: 'indices' | 'brackets' | 'repeat' | 'comma' = 'brackets',
): string {
  const prefix = isPrefix ? '?' : '';
  const result: string[] = [];
  const validFormats = ['indices', 'brackets', 'repeat', 'comma'];
  if (!validFormats.includes(arrayFormat)) arrayFormat = 'brackets';

  Object.entries(data).forEach(([key, value]) => {
    if (value === '' || value === undefined || value === null) return;
    if (Array.isArray(value)) {
      switch (arrayFormat) {
        case 'indices':
          value.forEach((v, i) => result.push(`${encodeURIComponent(key)}[${i}]=${encodeURIComponent(v)}`));
          break;
        case 'brackets':
          value.forEach((v) => result.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`));
          break;
        case 'repeat':
          value.forEach((v) => result.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`));
          break;
        case 'comma':
          result.push(`${encodeURIComponent(key)}=${value.map((v) => encodeURIComponent(v)).join(',')}`);
          break;
      }
    } else {
      result.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });
  return result.length ? prefix + result.join('&') : '';
}

/**
 * @description 解析a=1&b=2类型的参数
 * @param {string} queryStr 需要解析的参数字符串
 */
export function parseQueryParams(queryStr: string) {
  return queryStr.split('&').reduce((acc: Recordable, cur: string) => {
    const item: string[] = cur.split('=');
    acc[item[0]] = item[1];
    return acc;
  }, {});
}

/**
 * @description 通用深度克隆函数，支持处理 Date、RegExp、Map、Set、Array、Object 等特殊情况
 * @param obj 需要克隆的对象
 * @param hash
 * @returns 深度克隆后的新对象
 */
export function deepClone<T>(obj: T, hash = new WeakMap<object, any>()): T {
  // 只对对象和函数以外的类型直接返回
  if (typeof obj !== 'object' || obj === null) return obj;
  if (hash.has(obj as object)) return hash.get(obj as object);

  if (obj instanceof Date) return new Date(obj.getTime()) as any;
  if (obj instanceof RegExp) return new RegExp(obj) as any;
  if (obj instanceof Map) {
    const result = new Map();
    hash.set(obj as object, result);
    (obj as Map<any, any>).forEach((v, k) => result.set(deepClone(k, hash), deepClone(v, hash)));
    return result as any;
  }
  if (obj instanceof Set) {
    const result = new Set();
    hash.set(obj as object, result);
    (obj as Set<any>).forEach((v) => result.add(deepClone(v, hash)));
    return result as any;
  }
  if (Array.isArray(obj)) {
    const result: any[] = [];
    hash.set(obj as object, result);
    (obj as any[]).forEach((item, i) => {
      result[i] = deepClone(item, hash);
    });
    return result as any;
  }
  // 普通对象
  const result: Record<string, any> = Object.create(Object.getPrototypeOf(obj));
  hash.set(obj as object, result);
  for (const key of Reflect.ownKeys(obj)) {
    result[key as any] = deepClone((obj as any)[key], hash);
  }
  return result as T;
}

/**
 * @description 深度合并对象，支持数组、Date、Map、Set、循环引用等特殊情况
 * @param target 目标对象
 * @param sources 源对象（可多个）
 * @returns 合并后的新对象
 */
export function deepMerge<T extends object, S extends object[]>(target: T, ...sources: S): T & S[number] {
  const seen = new WeakMap();
  function _merge(obj1: any, obj2: any): any {
    if (obj1 === obj2) return obj1;
    if (obj2 instanceof Date) return new Date(obj2.getTime());
    if (obj2 instanceof RegExp) return new RegExp(obj2);
    if (obj2 instanceof Map) {
      const result = new Map(obj1 instanceof Map ? obj1 : undefined);
      obj2.forEach((v, k) => result.set(k, _merge(result.get(k), v)));
      return result;
    }
    if (obj2 instanceof Set) {
      const result = new Set(obj1 instanceof Set ? obj1 : undefined);
      obj2.forEach((v) => result.add(v));
      return result;
    }
    if (Array.isArray(obj2)) {
      // 合并数组时，采用覆盖策略
      return obj2.slice();
    }
    if (typeof obj2 === 'object' && obj2 !== null) {
      if (seen.has(obj2)) return seen.get(obj2);
      const result = typeof obj1 === 'object' && obj1 !== null ? { ...obj1 } : {};
      seen.set(obj2, result);
      Reflect.ownKeys(obj2).forEach((key) => {
        result[key as any] = _merge(result[key as any], obj2[key as any]);
      });
      return result;
    }
    return obj2;
  }
  return sources.reduce((acc, src) => _merge(acc, src), target) as T & S[number];
}

/**
 * @description 生成指定范围内的随机整数，支持负数和参数校验
 * @param min 最小值（包含）
 * @param max 最大值（包含）
 * @returns 随机整数
 */
export function random(min: number, max: number): number {
  if (typeof min !== 'number' || typeof max !== 'number' || isNaN(min) || isNaN(max)) return 0;
  if (min > max) [min, max] = [max, min];
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * @description 延时函数，支持 await/Promise，默认300ms，可自定义毫秒数
 * @param ms 延迟的毫秒数，默认300
 * @returns Promise<void>
 */
export function sleep(ms: number = 300): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * @description 获取当前页面路径
 */
export function page() {
  const pages = getCurrentPages();
  // 某些特殊情况下(比如页面进行redirectTo时的一些时机)，pages可能为空数组
  return `/${pages[pages.length - 1]?.route ?? ''}`;
}

/**
 * @description 添加单位，如果有rpx，upx，%，px等单位结尾或者值为auto，直接返回，否则加上px单位结尾
 * @param value 需要添加单位的值
 * @param unit 添加的单位名 比如rpx
 */
export function addUnit(value: string | number = 'auto', unit: 'rpx' | 'px' | 'upx' | '%' = 'rpx') {
  value = String(value);
  // 用内置验证规则中的number判断是否为数值
  return isNumber(value) ? `${value}${unit}` : value;
}

/**
 * @description vite 动态导入图片
 * @param filepath 图片路径
 */
export function getAssetsFile(filepath: string) {
  filepath = filepath.replace(/^\/+/, '');
  return new URL(`/static/${filepath}`, import.meta.url)?.href;
}

/**
 * @description 使用内置的图片预览功能
 * @param url 图片地址 字符串或数组
 * @param tips 提示词
 */
export function previewImages(url: string | string[], tips: boolean | string = false) {
  if (isEmpty(url)) {
    Boolean(tips) && toast(isString(tips) ? <string>tips : '图片地址异常');
    return;
  }

  const images: string[] = Array.isArray(url) ? url : [url];
  uni.previewImage({
    urls: images,
  });
}

/**
 * @description 去除字符串空格
 * @param str 需要去除空格的字符串或数字
 * @param pos 去除位置：both(两端)|left(左)|right(右)|all(全部)，默认both
 * @returns 去除空格后的字符串
 */
export function trim(str: string | number, pos: 'both' | 'left' | 'right' | 'all' = 'both'): string {
  const s = String(str);
  switch (pos) {
    case 'both':
      return s.replace(/^\s+|\s+$/g, '');
    case 'left':
      return s.replace(/^\s*/, '');
    case 'right':
      return s.replace(/(\s*$)/g, '');
    case 'all':
      return s.replace(/\s+/g, '');
    default:
      return s;
  }
}

/**
 * @description 样式转换：对象转字符串，或字符串转对象
 * @param customStyle 需要转换的目标对象或字符串
 * @param target 转换目标类型：object-转为对象，string-转为字符串，默认object
 * @returns 转换后的对象或字符串
 */
export function addStyle(
  customStyle: Record<string, string> | string,
  target: 'object' | 'string' = 'object',
): Record<string, string> | string {
  // 字符串转字符串，对象转对象情形，直接返回
  if (
    isEmpty(customStyle) ||
    (typeof customStyle === 'object' && target === 'object') ||
    (target === 'string' && typeof customStyle === 'string')
  ) {
    return customStyle;
  }

  // 字符串转对象
  if (target === 'object' && typeof customStyle === 'string') {
    const styleArray = trim(customStyle).split(';');
    const style: Record<string, string> = {};
    for (let i = 0; i < styleArray.length; i++) {
      if (styleArray[i]) {
        const item = styleArray[i].split(':');
        if (item.length === 2) {
          style[trim(item[0])] = trim(item[1]);
        }
      }
    }
    return style;
  }

  // 对象转字符串
  if (typeof customStyle === 'object' && target === 'string') {
    let string = '';
    for (const i in customStyle) {
      const key = i.replace(/([A-Z])/g, '-$1').toLowerCase();
      string += `${key}:${customStyle[i]};`;
    }
    return trim(string);
  }

  return customStyle;
}
